import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useConsultation } from "@/components/ConsultationProvider";
import { useContent } from "@/context/ContentProvider";
import { PROCESS } from "@/data/site";

export default function Services() {
  const { open } = useConsultation();
  const { services } = useContent();

  return (
    <div className="pt-16">
      <Seo
        title="Epoxy Flooring Services in Cleveland, OH | Crownvera Epoxy"
        description="Garage floor coatings, basement epoxy, patios, workshops, decorative flake, metallic finishes, concrete sealing & polishing across Cleveland & Northeast Ohio."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-20 pb-16" data-testid="services-hero">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Services</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 leading-tight max-w-3xl">
            Every surface, <span className="text-gold-shimmer italic">crowned.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">
            From one-day garage makeovers to hand-poured metallic masterpieces, every Crownvera system starts with diamond-ground prep and ends under a UV-stable polyaspartic shield.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-24" data-testid="services-list">
        <div className="grid gap-8 md:grid-cols-2">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 2) * 0.1}>
              <Link to={`/services/${s.slug}`} data-testid={`services-page-card-${s.slug}`} className="gold-ring-card group grid sm:grid-cols-2 rounded-lg overflow-hidden h-full">
                <div className="h-52 sm:h-full overflow-hidden">
                  <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-7 flex flex-col">
                  <h2 className="font-display text-xl text-amber-900">{s.name}</h2>
                  <p className="text-sm text-stone-500 mt-2 leading-relaxed">{s.tagline}</p>
                  <ul className="mt-4 space-y-1.5">
                    {(s.benefits || []).slice(0, 2).map((b) => (
                      <li key={b} className="flex items-center gap-2 text-xs text-stone-600">
                        <Check className="w-3.5 h-3.5 text-amber-700 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-auto pt-5 text-xs font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-2">
                    View system <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-amber-600/15 bg-[#F5F2EB]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">The Crownvera Process</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-stone-900 max-w-2xl leading-tight">
              Four layers between your concrete and the world
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.1}>
                <div className="gold-ring-card rounded-lg p-7 h-full" data-testid={`process-step-${i}`}>
                  <span className="font-display text-3xl text-gold-shimmer">{p.step}</span>
                  <h3 className="font-display text-lg text-amber-900 mt-4 mb-3">{p.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14 text-center">
            <button
              onClick={open}
              data-testid="services-page-cta-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 rounded-md shadow-md transition-colors duration-300"
            >
              Schedule Your Free Consultation
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
